import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject} from '@angular/core';
import {DialogModule, TooltipModule} from "@syncfusion/ej2-angular-popups";
import {StepHeaderComponent} from "../../../shared/elements/step-header/step-header.component";
import {DialogService} from "../../../shared/services/dialog.service";
import {LanguageService} from "../../../shared/services/language.service";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {FormDateTimeComponent} from "../../../shared/elements/form-date-time/form-date-time.component";
import {FormInputComponent} from "../../../shared/elements/form-input/form-input.component";
import {FormLabelComponent} from "../../../shared/elements/form-label/form-label.component";
import {FormRadioComponent} from "../../../shared/elements/form-radio/form-radio.component";
import {FormUserGroupMailComponent} from "../../../shared/elements/form-user-group-mail/form-user-group-mail.component";
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";
import {StepContentComponent} from "../../../shared/elements/step-content/step-content.component";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {DateTimePickerModule} from "@syncfusion/ej2-angular-calendars";
import {DropDownListModule} from "@syncfusion/ej2-angular-dropdowns";
import {RadioButtonModule} from "@syncfusion/ej2-angular-buttons";
import {TextBoxModule} from "@syncfusion/ej2-angular-inputs";
import {Subscription, take} from "rxjs";
import {ApiService} from "../../../shared/services/api.service";
import {DashboardLinkAdminResource} from '../../../shared/types/generated/dashboardLinkAdminResource';
import {SettingsDashboardLinkUpdateRequest} from '../../../shared/types/generated/settingsDashboardLinkUpdateRequest';
import {ToasterService} from '../../../shared/services/toaster.service';
import {StepItem} from '../../../shared/types/help-types/stepsInterface';
import {ContentComponent} from "../../../shared/elements/content/content.component";

@Component({
    selector: 'app-dialog-home-link',
    imports: [
    DialogModule,
    StepHeaderComponent,
    FormButtonComponent,
    FormDateTimeComponent,
    FormInputComponent,
    FormLabelComponent,
    FormRadioComponent,
    FormUserGroupMailComponent,
    HeadlineComponent,
    ReactiveFormsModule,
    StepContentComponent,
    TooltipComponent,
    TranslatePipe,
    DateTimePickerModule,
    DropDownListModule,
    RadioButtonModule,
    TextBoxModule,
    TooltipModule,
    ContentComponent
],
    templateUrl: './dialog-home-link.component.html',
    styleUrl: './dialog-home-link.component.scss'
})
export class DialogHomeLinkComponent implements OnInit, OnDestroy {
  public dialogService: DialogService = inject(DialogService);
  private languageService: LanguageService = inject(LanguageService);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private apiService: ApiService = inject(ApiService);
  private toaster: ToasterService = inject(ToasterService);


  @Input() public homeLinkToEdit: DashboardLinkAdminResource | null = null;
  @Input() public visible: boolean = false;
  @Output() public  homeLinkToEditChange: EventEmitter<DashboardLinkAdminResource> = new EventEmitter<DashboardLinkAdminResource>();
  @Output() public  visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public steps: StepItem[]  = [{id:'details', title:this.languageService.getTranslationByKey('HOMELINKDETAILS'), help:this.languageService.getTranslationByKey('HOMELINKDETAILSHELP')}, {id:'properties', title:this.languageService.getTranslationByKey('HOMELINKPROPERTIES'), help:this.languageService.getTranslationByKey('HOMELINKPROPERTIESHELP')}, {id:'sharing', title:this.languageService.getTranslationByKey('HOMELINKSHARE'), help:this.languageService.getTranslationByKey('HOMELINKSHAREHELP'), optional:true}];
  public activeStep: number = 0;
  public homeLinkForm: FormGroup = this.formBuilder.group({
    name: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(35)]],
    url: ['', [Validators.required, Validators.pattern(/^https:\/\/(?!(ww|w)\.)(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,63}([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/i)]],
    platform: ['both', [Validators.required]],
    visible_from: [''],
    visible_to: ['']
  });
  public participantsUserIDs: number[] = [];
  public participantsGroupIDs: number[] = [];
  public visibleToMinDate: Date = this.getVisibleToMinDate();
  private subscriptions: Subscription[] = [];

  public ngOnInit(): void {
    if (this.homeLinkToEdit) {
      this.homeLinkForm.patchValue({
        name: this.homeLinkToEdit.name,
        url: this.homeLinkToEdit.url,
        platform: this.homeLinkToEdit.on_mobile && this.homeLinkToEdit.on_web ? 'both' : this.homeLinkToEdit.on_mobile ? 'mobile' : 'desktop',
        visible_from: this.homeLinkToEdit.show_at ? new Date(this.homeLinkToEdit.show_at) : '',
        visible_to: this.homeLinkToEdit.show_until ? new Date(this.homeLinkToEdit.show_until) : ''
      });
      this.participantsUserIDs = this.homeLinkToEdit.user_ids.map(id => typeof id === 'string' ? parseInt(id) : id);
      this.participantsGroupIDs = this.homeLinkToEdit.group_ids.map(id => typeof id === 'string' ? parseInt(id) : id);
    }

    this.updateVisibleToConstraint(false);
    this.subscriptions.push(
      this.homeLinkForm.controls['visible_from'].valueChanges.subscribe(() => this.updateVisibleToConstraint()),
      this.homeLinkForm.controls['visible_to'].valueChanges.subscribe(() => this.updateVisibleToConstraint())
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public get stepTitles(): string[] {
    return this.steps?.map(step => step.title) ?? [];
  }

  public handleStepChange(step: number | null = null): void {
    const targetStep = step ?? this.activeStep + 1;

    if (targetStep < 0 || targetStep >= this.steps.length) {
      return;
    }

    if (targetStep < this.activeStep) {
      this.activeStep = targetStep;
      return;
    }

    while (this.activeStep < targetStep) {
      const currentStep = this.steps[this.activeStep];

      if (!currentStep) {
        return;
      }

      if (
        currentStep.id === 'details' &&
        (
          this.homeLinkForm.controls['name'].invalid ||
          this.homeLinkForm.controls['url'].invalid
        )
      ) {
        this.homeLinkForm.controls['name'].markAsDirty();
        this.homeLinkForm.controls['url'].markAsDirty();
        return;
      }

      this.activeStep++;
    }
  }

  public save(): void {
    if (this.homeLinkForm.invalid) return;

    const mappedData: SettingsDashboardLinkUpdateRequest = {
      name: this.homeLinkForm.controls['name'].value,
      url: this.homeLinkForm.controls['url'].value,
      on_web: this.homeLinkForm.controls['platform'].value === 'both' || this.homeLinkForm.controls['platform'].value === 'desktop',
      on_mobile: this.homeLinkForm.controls['platform'].value === 'both' || this.homeLinkForm.controls['platform'].value === 'mobile',
      show_at: this.homeLinkForm.controls['visible_from'].value ? new Date(this.homeLinkForm.controls['visible_from'].value).toISOString() : null,
      show_until: this.homeLinkForm.controls['visible_to'].value ? new Date(this.homeLinkForm.controls['visible_to'].value).toISOString() : null,
      user_ids: this.participantsUserIDs,
      group_ids: this.participantsGroupIDs
    };

    this.apiService.createOrUpdateSettingsHomeLink(mappedData, this.homeLinkToEdit !== null ? this.homeLinkToEdit.id : null)
    .pipe(take(1))
    .subscribe({
      next: () => { 
        if(this.homeLinkToEdit !== null) {
          this.toaster.success(
            this.languageService.getTranslationByKey('HOMELINKUPDATEDTITLE'),
            this.languageService.getTranslationByKey('HOMELINKUPDATEDMESSAGE')
          );
        } else {
          this.toaster.success(
            this.languageService.getTranslationByKey('HOMELINKCREATEDTITLE'),
            this.languageService.getTranslationByKey('HOMELINKCREATEDMESSAGE')
          );
        }
        this.close();
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  public close(): void {
    this.homeLinkForm.reset({
      name: '',
      url: '',
      platform: 'both',
      visible_from: '',
      visible_to: ''
    });
    this.participantsUserIDs = [];
    this.participantsGroupIDs = [];
    this.activeStep = 0;
    this.visibleChange.emit(false);
  }

  private updateVisibleToConstraint(emitEvent: boolean = false): void {
    this.visibleToMinDate = this.getVisibleToMinDate(this.homeLinkForm.controls['visible_from'].value);
    const visibleTo = this.homeLinkForm.controls['visible_to'].value;
    if (visibleTo && new Date(visibleTo) < this.visibleToMinDate) {
      this.homeLinkForm.controls['visible_to'].setValue(new Date(this.visibleToMinDate), {emitEvent});
    }
  }

  private getVisibleToMinDate(visibleFrom: string | Date | null = null): Date {
    const minDate = visibleFrom ? new Date(visibleFrom) : new Date();
    minDate.setDate(minDate.getDate() + 1);
    return minDate;
  }

}
